/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCreateEventContext } from "../../context/CreateEventContext";
import ModalLoadingState from "../PopUpModal/ModalLoadingState";
import { getUserEvents } from "../../firebaseFunctions/getUserEvents/getUserEvents";
import { getEOEventApplications } from "../../firebaseFunctions/getEOEventApplications/getEOEventApplications";
import { updateMPSApplication } from "../../firebaseFunctions/updateMPSApplication";
import placeholderImage from "../../assets/images/no_image_placeholder.jpg";
import { formatEventDateRange } from "../../utils/dateAndTime";
import { PebbleTable } from "../ui/TableComponents";
import ReferralMetrics from "./ReferralMetrics";
import ReferralCard from "./ReferralCard";
import { ArrowRightIcon } from "@heroicons/react/solid";
import QuickActionModal from "./QuickActionModal";
import { Sun, Moon, Globe } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import ScrollIndicator from "../ui/ScrollIndicator";
import { useTranslation } from "react-i18next";
import { X, Zap } from "lucide-react";

// RecentEvents component
const RecentEvents = ({ events = [] }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <div className="bg-base-white w-full rounded-xl shadow-sm border border-grey-200 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-grey-900">
          {t('dashboard.recentEvents')}
          <span className="ml-2 text-sm font-normal text-grey-500">
            ({events.length})
          </span>
        </h2>
      </div>
      {events.length === 0 ? (
        <div className="text-center py-12">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-grey-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-grey-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <p className="text-base font-medium mb-2 text-grey-900">
              {t('dashboard.noUpcomingEvents')}
            </p>
            <p className="text-sm text-grey-500">
              {t('dashboard.clickToCreate')}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3 overflow-y-auto max-h-[400px]">
          {events.map((event, index) => (
            <div
              key={index}
              className="p-4 rounded-lg border border-grey-200 hover:border-grey-300 hover:shadow-sm transition-all duration-200 cursor-pointer group"
              onClick={() => navigate(`/eo/events/${event.eventId}`)}
            >
              <div className="flex items-center gap-4">
                <img
                  src={event.eventCoverPhoto || placeholderImage}
                  alt={event.eventName}
                  className="h-20 w-20 object-cover rounded-lg flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold text-grey-900 mb-1 truncate">
                    {event.eventName}
                  </p>
                  <p className="text-sm text-grey-600 mb-2">
                    {formatEventDateRange(
                      event.loadInDate,
                      event.loadInTime || "00:00",
                      event.loadOutDate,
                      event.loadOutTime || "23:59",
                    )}
                  </p>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-success-50 text-success-700 border border-success-200">
                    <span className="mr-1.5 text-lg leading-none">•</span>
                    {t('dashboard.upcoming')}
                  </span>
                </div>
                <div className="flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-grey-400 group-hover:text-grey-600 transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// LiveEventSnapshot — 3 cards scoped to a single selected event
const LiveEventSnapshot = ({ liveEvents = [], selectedEventId, onSelectEvent, onQuickAction }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [infoOpen, setInfoOpen] = React.useState(false);
  const infoRef = React.useRef(null);

  const selectedEvent = liveEvents.find(e => e.id === selectedEventId) || liveEvents[0];
  const apps = selectedEvent?.mpsregistrations || [];

  const registered = apps.filter(r => r.applicationStatus?.toLowerCase() === 'registered').length;
  const paid = apps.filter(r => r.paymentStatus?.toLowerCase() === 'paid').length;
  const capacity = parseInt(selectedEvent?.sellerCapacity) || 0;
  const spotsLeft = capacity > 0 ? Math.max(0, capacity - registered) : null;

  // Close info popover on outside click
  React.useEffect(() => {
    const handler = (e) => { if (infoRef.current && !infoRef.current.contains(e.target)) setInfoOpen(false); };
    if (infoOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [infoOpen]);

  const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const cardClass = "bg-white rounded-xl border border-grey-100 p-4 hover:border-grey-300 hover:shadow-md transition-all duration-200 cursor-pointer group";
  const iconClass = "p-1.5 rounded-lg bg-grey-100 text-grey-700 group-hover:scale-110 transition-transform";
  const titleClass = "text-xs font-medium text-grey-500 uppercase tracking-wide";
  const valueClass = "text-2xl font-semibold text-grey-900 mb-1";
  const subtitleClass = "text-xs text-grey-400 group-hover:text-grey-600 transition-colors";

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-2">
        {/* Left: fixed label — never changes, no reflow */}
        <h3 className="text-xs font-semibold text-grey-500 uppercase tracking-wide whitespace-nowrap">
          {t('dashboard.liveEventSnapshot')}
        </h3>

        <div className="flex items-center gap-2 min-w-0">
          {/* Event selector dropdown — fixed height, no layout shift */}
          {liveEvents.length > 0 && (
            <select
              value={selectedEvent?.id || ''}
              onChange={(e) => onSelectEvent(e.target.value)}
              className="text-xs font-medium text-grey-800 bg-grey-50 border border-grey-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer min-w-0 max-w-[200px] truncate"
            >
              {liveEvents.map(event => (
                <option key={event.id} value={event.id}>
                  {event.eventName || t('dashboard.unnamedEvent')}
                </option>
              ))}
            </select>
          )}

          {/* Info icon — shows event details popover */}
          {selectedEvent && (
            <div className="relative flex-shrink-0" ref={infoRef}>
              <button
                onClick={() => setInfoOpen(v => !v)}
                className={`p-1.5 rounded-lg transition-colors ${infoOpen ? 'bg-primary-100 text-primary-600' : 'text-grey-400 hover:text-grey-600 hover:bg-grey-100'}`}
                title="Event details"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>

              {infoOpen && (
                <div className="absolute right-0 top-full mt-1.5 w-64 bg-white rounded-xl shadow-lg border border-grey-200 p-4 z-50 text-left">
                  <p className="text-xs font-semibold text-grey-900 mb-3 truncate">{selectedEvent.eventName}</p>
                  <div className="space-y-2">
                    {[
                      { label: 'Date', value: selectedEvent.startDate === selectedEvent.endDate ? fmtDate(selectedEvent.startDate) : `${fmtDate(selectedEvent.startDate)} – ${fmtDate(selectedEvent.endDate)}` },
                      { label: 'Time', value: selectedEvent.startTime && selectedEvent.endTime ? `${selectedEvent.startTime} – ${selectedEvent.endTime}` : '—' },
                      { label: 'Location', value: selectedEvent.location || '—' },
                      { label: 'Apply by', value: fmtDate(selectedEvent.deadlineDate) },
                      { label: 'Capacity', value: capacity > 0 ? `${registered} / ${capacity} vendors` : 'No limit set' },
                      { label: 'Fee', value: selectedEvent.registrationFee ? `$${selectedEvent.registrationFee}` : 'Free' },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex justify-between gap-2">
                        <span className="text-xs text-grey-400 flex-shrink-0">{label}</span>
                        <span className="text-xs text-grey-700 text-right truncate">{value}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => { navigate(`/eo/events/${selectedEvent.eventId || selectedEvent.id}`); setInfoOpen(false); }}
                    className="mt-3 w-full text-xs text-primary-600 hover:text-primary-700 font-medium text-center py-1.5 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
                  >
                    View full event →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {liveEvents.length === 0 ? (
        <div className="bg-white rounded-xl border border-grey-100 p-6 text-center">
          <p className="text-sm font-medium text-grey-500">{t('dashboard.noActiveEvent')}</p>
          <p className="text-xs text-grey-400 mt-1">{t('dashboard.noActiveEventDesc')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Registered */}
          <div onClick={onQuickAction} className={cardClass}>
            <div className="flex items-center justify-between mb-3">
              <span className={titleClass}>{t('dashboard.registeredVendors')}</span>
              <div className={iconClass}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>
            <p className={valueClass}>{registered}</p>
            <p className={subtitleClass}>{t('dashboard.signedUpForEvent')}</p>
          </div>

          {/* Paid */}
          <div onClick={onQuickAction} className={cardClass}>
            <div className="flex items-center justify-between mb-3">
              <span className={titleClass}>{t('dashboard.paid')}</span>
              <div className={iconClass}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className={valueClass}>{paid}</p>
            <p className={subtitleClass}>{t('dashboard.paymentReceived')}</p>
          </div>

          {/* Spots Left */}
          <div onClick={() => navigate(`/eo/events/${selectedEvent?.id}`)} className={cardClass}>
            <div className="flex items-center justify-between mb-3">
              <span className={titleClass}>{t('dashboard.spotsLeft')}</span>
              <div className={iconClass}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            <p className={valueClass}>{spotsLeft !== null ? spotsLeft : '—'}</p>
            <p className={subtitleClass}>
              {capacity > 0
                ? `${registered} / ${capacity} ${t('dashboard.spots')}`
                : t('dashboard.setCapacityHint')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// AllTimeStats — quieter aggregate tiles below the live snapshot
const AllTimeStats = ({ totalEvents = 0, totalVendors = 0, totalRevenue = 0 }) => {
  const { t } = useTranslation();
  const stats = [
    {
      title: t('dashboard.eventsHosted'),
      value: totalEvents,
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      title: t('dashboard.totalVendors'),
      value: totalVendors,
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      title: t('dashboard.totalRevenue'),
      value: `$${totalRevenue.toLocaleString()}`,
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
    },
  ];

  return (
    <div>
      <h3 className="text-xs font-semibold text-grey-500 uppercase tracking-wide mb-3">
        {t('dashboard.allTimeStats')}
      </h3>
      <div className="grid grid-cols-3 gap-3">
        {stats.map((stat, i) => (
          <div key={i} className="bg-grey-50 rounded-xl border border-grey-100 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-grey-500 uppercase tracking-wide">{stat.title}</span>
              <div className="p-1.5 rounded-lg bg-white text-grey-500">
                {stat.icon}
              </div>
            </div>
            <p className="text-xl font-semibold text-grey-900">{stat.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const MarketPlaceSellers = ({ sellers = [], noDataAction }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const onColumnClick = (id) => {
    navigate(`/eo/marketplace/${id}`);
  };
  const columns = [
    {
      header: t('dashboard.company'),
      key: "company",
      type: "text",
      primaryKey: "company",
    },
    {
      header: t('dashboard.status'),
      key: "status",
      type: "status",
      statusType: "application",
    },
    {
      header: t('dashboard.payment'),
      key: "payment",
      type: "status",
      statusType: "payment",
    },
    {
      header: t('dashboard.contact'),
      key: "contact",
      type: "compound",
      primaryKey: "phone",
      secondaryKey: "email",
    },
    {
      header: t('dashboard.boothNumber'),
      key: "boothNumber",
      type: "text",
    },
  ];

  return (
    <div className="my-8">
      <h2 className="text-lg font-semibold text-grey-900 mb-5">
        {t('dashboard.marketplaceVendors')}
      </h2>
      <PebbleTable
        columns={columns}
        data={sellers}
        noDataTitle={t('dashboard.noMarketplaceVendors')}
        noDataSubTitle={t('dashboard.noMarketplaceVendorsDesc')}
        onColumnClick={onColumnClick}
      />
    </div>
  );
};

const EventsOverview = React.memo(
  ({ events = [], statusCounts, applicationsByEvent = [] }) => {
    const eventCounts = useEventCounts(events);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const applicationCategories = React.useMemo(
      () => [
        { label: t('dashboard.allApplications'), count: statusCounts?.all || 0 },
        { label: t('dashboard.applied'), count: statusCounts?.pending || 0 },
        { label: t('dashboard.inProgress'), count: statusCounts?.inprogress || 0 },
        { label: t('dashboard.registered'), count: statusCounts?.registered || 0 },
        { label: t('dashboard.declined'), count: statusCounts?.declined || 0 },
      ],
      [statusCounts, t],
    );

    return (
      <div>
        <h2 className="text-lg font-semibold text-grey-900 mb-5">
          {t('dashboard.eventsOverview')}
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-base-white rounded-xl shadow-sm border border-grey-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-grey-200 bg-grey-50">
              <h3 className="text-sm font-semibold text-grey-900">
                {t('dashboard.allEvents')}
              </h3>
            </div>
            {[
              { label: t('dashboard.all'), count: eventCounts.all },
              { label: t('dashboard.upcoming'), count: eventCounts.upcoming },
              { label: t('dashboard.ongoing'), count: eventCounts.ongoing },
              { label: t('dashboard.archived'), count: eventCounts.archived },
              { label: t('dashboard.draft'), count: eventCounts.draft },
            ].map((category, index) => (
              <div
                key={index}
                className="flex items-center justify-between px-5 py-3 hover:bg-grey-50 cursor-pointer transition-colors group border-b border-grey-100 last:border-b-0"
                onClick={() => navigate("/eo/events")}
              >
                <span className="text-sm text-grey-700 font-medium">
                  {category.label}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-grey-900 bg-grey-100 px-2.5 py-0.5 rounded-full">
                    {category.count}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-grey-400 group-hover:text-grey-600 transition-colors"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-base-white rounded-xl shadow-sm border border-grey-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-grey-200 bg-grey-50">
              <h3 className="text-sm font-semibold text-grey-900">
                {t('dashboard.vendorApplications')}
              </h3>
            </div>
            {applicationCategories.map((category, index) => (
              <div
                key={index}
                className="flex items-center justify-between px-5 py-3 hover:bg-grey-50 cursor-pointer transition-colors border-b border-grey-100 last:border-b-0"
                onClick={() => navigate("/eo/marketplace")}
              >
                <span className="text-sm text-grey-700 font-medium">
                  {category.label}
                </span>
                <span className="text-sm font-semibold text-grey-900 bg-grey-100 px-2.5 py-0.5 rounded-full">
                  {category.count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {applicationsByEvent.length > 0 && (
          <div className="mt-4 bg-base-white rounded-xl shadow-sm border border-grey-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-grey-200 bg-grey-50">
              <h3 className="text-sm font-semibold text-grey-900">
                {t('dashboard.applicationsByEvent')}
              </h3>
            </div>
            <div className="max-h-[200px] overflow-y-auto">
              {applicationsByEvent.map((event, index) => (
                <div
                  key={event.eventId || index}
                  className="flex items-center justify-between px-5 py-3 hover:bg-grey-50 cursor-pointer transition-colors border-b border-grey-100 last:border-b-0 group"
                  onClick={() => navigate(`/eo/events/${event.eventId}`)}
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-grey-700 font-medium truncate block">
                      {event.eventName}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {event.pendingCount > 0 && (
                      <span className="text-xs font-medium text-warning-600 bg-warning-50 px-2 py-0.5 rounded-full">
                        {event.pendingCount} {t('dashboard.pendingStatus')}
                      </span>
                    )}
                    <span className="text-sm font-semibold text-grey-900 bg-grey-100 px-2.5 py-0.5 rounded-full">
                      {event.totalCount} {t('dashboard.totalStatus')}
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-grey-400 group-hover:text-grey-600 transition-colors"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  },
);

// Plan upsell banner — shown to Basic users until dismissed
const PlanUpsellBanner = ({ userId, onDismiss }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <div className="mx-3 mt-3 sm:mx-6 sm:mt-4 bg-grey-900 text-white rounded-2xl px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div className="flex items-start sm:items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
          <Zap className="w-5 h-5 text-yellow-300" />
        </div>
        <div>
          <p className="font-semibold text-sm">{t('dashboard.basicPlanBanner')}</p>
          <p className="text-xs text-grey-300 mt-0.5">
            {t('dashboard.upgradeToProDesc')}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <div className="hidden sm:flex items-center gap-4 text-xs text-grey-400 mr-2">
          <span>✓ {t('dashboard.eventsPerMonth')}</span>
          <span className="text-grey-600">→</span>
          <span className="text-yellow-300">✓ {t('dashboard.unlimited')}</span>
          <span className="text-grey-600">+</span>
          <span className="text-yellow-300">✓ {t('dashboard.ticketing')}</span>
          <span className="text-grey-600">+</span>
          <span className="text-yellow-300">✓ {t('dashboard.signing')}</span>
        </div>
        <button
          onClick={() => navigate('/eo/settings?tab=subscription')}
          className="px-4 py-2 bg-white text-grey-900 text-sm font-semibold rounded-xl hover:bg-grey-100 transition-colors whitespace-nowrap"
        >
          {t('dashboard.upgradeToPro')}
        </button>
        <button
          onClick={onDismiss}
          className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-grey-400" />
        </button>
      </div>
    </div>
  );
};

// Main DashboardContent component
function DashboardContent() {
  const navigate = useNavigate();
  const { user, subscriptionTier } = useAuth();
  const { resetEventData } = useCreateEventContext();
  const UserID = user.uid;
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { t, i18n } = useTranslation();
  const [userEvents, setUserEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [mpsDetailsForAllEvents, setMPSDetailsForAllEvents] = useState([]);
  const [quickActionModalOpen, setQuickActionModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  const BANNER_KEY = `pebble_plan_banner_dismissed_${UserID}`;
  const isFreeTier = !subscriptionTier || subscriptionTier === 'free';
  const isPro = subscriptionTier === 'pro' || subscriptionTier === 'premium' || subscriptionTier === 'enterprise';
  const [showUpsellBanner, setShowUpsellBanner] = useState(
    isFreeTier && !localStorage.getItem(BANNER_KEY)
  );
  const CRM_NUDGE_KEY = `pebble_crm_nudge_dismissed_${UserID}`;
  const [showCRMNudge, setShowCRMNudge] = useState(
    isPro && !localStorage.getItem(CRM_NUDGE_KEY)
  );

  const DRAFTS_BANNER_KEY = `pebble_drafts_banner_dismissed_${UserID}`;
  const [draftsBannerDismissed, setDraftsBannerDismissed] = useState(
    !!localStorage.getItem(DRAFTS_BANNER_KEY)
  );

  const handleDismissDraftsBanner = (e) => {
    e.stopPropagation();
    localStorage.setItem(DRAFTS_BANNER_KEY, '1');
    setDraftsBannerDismissed(true);
  };

  const handleDismissBanner = () => {
    localStorage.setItem(BANNER_KEY, '1');
    setShowUpsellBanner(false);
  };

  const handleDismissCRMNudge = () => {
    localStorage.setItem(CRM_NUDGE_KEY, '1');
    setShowCRMNudge(false);
  };

  const handleCreateEvent = () => {
    resetEventData();
    navigate("/eo/create-event");
  };

  const toggleLanguage = () => {
    const newLanguage = i18n.language === "en" ? "es" : "en";
    i18n.changeLanguage(newLanguage);
  };

  const displayEvents = async () => {
    setIsLoading(true);
    const response = await getUserEvents(UserID);
    if (Array.isArray(response)) {
      setUserEvents(response);
      setIsLoading(false);
    } else {
      setUserEvents([]);
      setIsLoading(false);
    }
  };

  const filterEvents = (events) => {
    const now = new Date();

    const filtered = events.filter((event) => {
      if (event.isDraft) return false;
      const loadIn = new Date(
        `${event.loadInDate}T${event.loadInTime || "00:00"}`,
      );
      const end = new Date(`${event.endDate}T${event.endTime || "23:59"}`);
      return loadIn > now || (loadIn <= now && end >= now);
    });

    setFilteredEvents(filtered);
  };

  useEffect(() => {
    filterEvents(userEvents);
  }, [userEvents]);

  useEffect(() => {
    displayEvents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (UserID) {
      const getAllMPSEventDetails = async () => {
        const response = await getEOEventApplications(UserID);
        if (response.success && Array.isArray(response.data)) {
          setMPSDetailsForAllEvents(response.data); // Set only if response.data is an array
        } else {
          console.log(
            "Error fetching event applications or data is not an array:",
            response.message,
          );
          setMPSDetailsForAllEvents([]); // Ensure it's an array if there's an error
        }
      };
      getAllMPSEventDetails();
    }
  }, [UserID]);
  const allSellersData =
    mpsDetailsForAllEvents?.flatMap((eventDetails) =>
      eventDetails.applications && Array.isArray(eventDetails.applications)
        ? eventDetails.applications.map((application) => ({
            company: application.businessName,
            status: application.applicationStatus,
            payment: application.paymentStatus,
            phone: application.businessPhoneNumber,
            email: application.businessEmail,
            boothNumber: application?.boothNumber || "-",
            id: application.id,
          }))
        : [],
    ) || [];

  const getStatusCounts = () => {
    return {
      all: allSellersData.length,
      pending: allSellersData.filter(
        (seller) => seller.status?.toLowerCase() === "applied",
      ).length,
      inprogress: allSellersData.filter(
        (seller) => seller.status?.toLowerCase() === "approved",
      ).length,
      registered: allSellersData.filter(
        (seller) => seller.status?.toLowerCase() === "registered",
      ).length,
      declined: allSellersData.filter(
        (seller) => seller.status?.toLowerCase() === "declined",
      ).length,
    };
  };
  const statusCounts = React.useMemo(() => getStatusCounts(), [allSellersData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live events sorted soonest first; default selection to the first one
  // Only show current/upcoming events — exclude past events (end date before today)
  const liveEvents = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return userEvents
      .filter(e => {
        if (e.isDraft) return false;
        const endDate = e.endDate || e.startDate;
        if (!endDate) return true;
        return new Date(endDate + 'T23:59:59') >= today;
      })
      .sort((a, b) => new Date(`${a.loadInDate}T${a.loadInTime || '00:00'}`) - new Date(`${b.loadInDate}T${b.loadInTime || '00:00'}`));
  }, [userEvents]);

  useEffect(() => {
    if (liveEvents.length > 0 && !selectedEventId) {
      setSelectedEventId(liveEvents[0].id);
    }
  }, [liveEvents]); // eslint-disable-line react-hooks/exhaustive-deps

  // All-time revenue: sum of (paid registrations × registrationFee) across all events
  const totalRevenue = React.useMemo(() => {
    return userEvents.filter(e => !e.isDraft).reduce((sum, event) => {
      const fee = parseFloat(event.registrationFee) || 0;
      const paidCount = (event.mpsregistrations || []).filter(
        r => r.paymentStatus?.toLowerCase() === 'paid'
      ).length;
      return sum + fee * paidCount;
    }, 0);
  }, [userEvents]);

  const applicationsByEvent = React.useMemo(() => {
    return mpsDetailsForAllEvents
      .map((event) => {
        const applications = Array.isArray(event.applications)
          ? event.applications
          : [];
        return {
          eventId: event.eventId,
          eventName: event.eventName || t('dashboard.unnamedEvent'),
          totalCount: applications.length,
          pendingCount: applications.filter(
            (app) => {
              const s = app.applicationStatus?.toLowerCase();
              return s === "applied" || s === "pending";
            },
          ).length,
        };
      })
      .filter((event) => event.totalCount > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpsDetailsForAllEvents]);

  const refreshApplications = async () => {
    const response = await getEOEventApplications(UserID);
    if (response.success && Array.isArray(response.data)) {
      setMPSDetailsForAllEvents(response.data);
    }
  };

  const handleApproveApplication = async (applicationId) => {
    try {
      const result = await updateMPSApplication(
        { applicationStatus: "Approved" },
        applicationId,
      );
      if (result.success) {
        await refreshApplications();
        showToast("Application approved!", "success", { label: "Assign Booth →", href: "/eo/booths" });
      } else {
        showToast("Failed to approve application", "error");
      }
    } catch (error) {
      console.error("Error approving application:", error);
      showToast("Failed to approve application", "error");
    }
  };

  const handleDenyApplication = async (applicationId) => {
    try {
      const result = await updateMPSApplication(
        { applicationStatus: "Declined" },
        applicationId,
      );
      if (result.success) {
        await refreshApplications();
        showToast("Application denied", "success");
      } else {
        showToast("Failed to deny application", "error");
      }
    } catch (error) {
      console.error("Error denying application:", error);
      showToast("Failed to deny application", "error");
    }
  };

  const handleCancelAndRefund = async (applicationId, applicationData) => {
    try {
      const result = await updateMPSApplication(
        {
          applicationStatus: "Cancelled",
          paymentStatus: "Refund Pending",
          cancelledAt: new Date().toISOString(),
          refundRequested: true,
          refundRequestedAt: new Date().toISOString(),
        },
        applicationId,
      );
      if (result.success) {
        await refreshApplications();
        showToast(
          "Registration cancelled - refund will be processed within 5-10 business days",
          "success",
        );
      } else {
        showToast("Failed to cancel registration", "error");
      }
    } catch (error) {
      console.error("Error cancelling registration:", error);
      showToast("Failed to cancel registration", "error");
    }
  };

  const showToast = (message, type = "info", cta = null) => {
    setToastMessage({ message, type, cta });
    setTimeout(() => setToastMessage(null), 4000);
  };

  const handleOpenQuickActions = () => {
    setQuickActionModalOpen(true);
  };

  return (
    <>
      {isLoading ? (
        <ModalLoadingState />
      ) : (
        <div className="w-full h-full flex flex-col bg-grey-50 relative">
          <ScrollIndicator storageKey="eo_dashboard_scroll_hint" />
          <div className="px-3 py-4 sm:px-6 sm:py-6 bg-base-white border-b border-grey-200">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-grey-900">
                  {t('dashboard.welcomeBack', { name: user?.displayName || user?.businessName || 'there' })}
                </h1>
                <p className="text-sm text-grey-600 mt-1">
                  {t('dashboard.eventsToday')}
                </p>
              </div>
            </div>
          </div>

          {showUpsellBanner && (
            <PlanUpsellBanner userId={UserID} onDismiss={handleDismissBanner} />
          )}

          <div className="p-3 sm:p-4 lg:p-6 flex-grow flex flex-col lg:flex-row overflow-hidden gap-6">
            <div className="w-full lg:w-1/3 flex-grow flex flex-col gap-5 overflow-y-auto pr-0 lg:pr-2">
              {userEvents.filter((e) => e.isDraft).length > 0 && !draftsBannerDismissed && (
                <div
                  onClick={() => navigate("/eo/events?tab=draft")}
                  className="bg-amber-50 border border-amber-200 rounded-xl p-4 cursor-pointer hover:bg-amber-100 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-5 h-5 text-amber-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-amber-900">
                          {userEvents.filter((e) => e.isDraft).length} {t('dashboard.draftsSaved')}
                        </p>
                        <p className="text-xs text-amber-700">
                          {t('dashboard.continueWhereLeft')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-amber-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                      <button
                        onClick={handleDismissDraftsBanner}
                        className="ml-1 p-1 rounded-full text-amber-500 hover:text-amber-700 hover:bg-amber-200 transition-colors"
                        aria-label="Dismiss"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {/* Vendor CRM nudge — shows for Pro users when they have applications */}
              {showCRMNudge && (statusCounts?.all || 0) > 0 && (
                <div className="bg-white border border-grey-200 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-9 h-9 bg-grey-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-grey-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-grey-900">{t('dashboard.buildVendorDirectory')}</p>
                    <p className="text-xs text-grey-500 mt-0.5">
                      {t('dashboard.vendorCrmNudge', { count: statusCounts?.all, s: statusCounts?.all !== 1 ? 's' : '' })}
                    </p>
                    <button
                      onClick={() => { handleDismissCRMNudge(); navigate('/eo/vendor-crm'); }}
                      className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 bg-grey-900 text-white text-xs font-medium rounded-lg hover:bg-grey-800 transition"
                    >
                      {t('dashboard.goToVendorCrm')}
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                  <button onClick={handleDismissCRMNudge} className="text-grey-300 hover:text-grey-500 transition shrink-0">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              <RecentEvents events={filteredEvents} />
              <ReferralCard userDetails={user} />
              <ReferralMetrics referralCount={user?.referralCount || 0} />
            </div>

            <div className="w-full lg:w-2/3 overflow-y-auto pl-0 lg:pl-2">
              <div className="mb-4">
                <LiveEventSnapshot
                  liveEvents={liveEvents}
                  selectedEventId={selectedEventId}
                  onSelectEvent={setSelectedEventId}
                  onQuickAction={handleOpenQuickActions}
                />
              </div>
              <div className="mb-6">
                <AllTimeStats
                  totalEvents={userEvents.filter(e => !e.isDraft).length}
                  totalVendors={allSellersData.length}
                  totalRevenue={totalRevenue}
                />
              </div>
              <MarketPlaceSellers
                sellers={allSellersData}
                noDataAction={handleCreateEvent}
              />
              <div>
                <EventsOverview
                  events={userEvents}
                  statusCounts={statusCounts}
                  applicationsByEvent={applicationsByEvent}
                />
              </div>
            </div>
          </div>

        </div>
      )}

      <QuickActionModal
        isOpen={quickActionModalOpen}
        onClose={() => setQuickActionModalOpen(false)}
        applications={allSellersData}
        onApprove={handleApproveApplication}
        onDeny={handleDenyApplication}
        onCancelAndRefund={handleCancelAndRefund}
      />

      {toastMessage && (
        <div
          className={`fixed bottom-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium transition-all duration-300 flex items-center gap-3 ${
            toastMessage.type === "success"
              ? "bg-green-600"
              : toastMessage.type === "error"
              ? "bg-red-600"
              : "bg-grey-700"
          }`}
        >
          <span>{toastMessage.message}</span>
          {toastMessage.cta && (
            <button
              onClick={() => { navigate(toastMessage.cta.href); setToastMessage(null); }}
              className="underline text-white text-xs font-semibold whitespace-nowrap opacity-90 hover:opacity-100"
            >
              {toastMessage.cta.label}
            </button>
          )}
        </div>
      )}
    </>
  );
}

export default DashboardContent;

const useEventCounts = (events) => {
  const [eventCounts, setEventCounts] = useState({
    all: 0,
    upcoming: 0,
    ongoing: 0,
    archived: 0,
    draft: 0,
  });

  useEffect(() => {
    if (!Array.isArray(events)) return;

    const now = new Date();
    const counts = events.reduce(
      (acc, event) => {
        const loadIn = new Date(
          `${event.loadInDate}T${event.loadInTime || "00:00"}`,
        );
        const loadOut = new Date(
          `${event.loadOutDate}T${event.loadOutTime || "23:59"}`,
        );

        acc.all += 1;
        if (loadIn > now) acc.upcoming += 1;
        if (loadIn <= now && loadOut >= now) acc.ongoing += 1;
        if (loadOut < now) acc.archived += 1;
        if (event.isDraft) acc.draft += 1;

        return acc;
      },
      { all: 0, upcoming: 0, ongoing: 0, archived: 0, draft: 0 },
    );
    setEventCounts(counts);
  }, [events]);

  return eventCounts;
};
